import type { Questionnaire } from '@medplum/fhirtypes'
import { VALIDATED_TOOL_TAG } from '#src/models/questionnaires/utils/index.ts'
import { QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL } from '#src/structureDefinitions.ts'

// Taken from https://lhcformbuilder.nlm.nih.gov/
export const phq9Questionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/PHQ-9',
  title: 'Patient Health Questionnaire (PHQ-9)',
  name: 'PHQ-9',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    profile: ['http://hl7.org/fhir/4.0/StructureDefinition/Questionnaire'],
    tag: [
      VALIDATED_TOOL_TAG,
      {
        code: 'lformsVersion: 36.7.0',
      },
    ],
  },
  copyright:
    'Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute.',
  code: [
    {
      system: 'http://loinc.org',
      code: '44249-1',
      display: 'PHQ-9 quick depression assessment panel [Reported.PHQ]',
    },
  ],
  item: [
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'PHQ-9 PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString:
            'https://www.apa.org/depression-guideline/patient-health-questionnaire.pdf',
        },
      ],
    },
    {
      type: 'choice',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44250-9',
      code: [
        {
          system: 'http://loinc.org',
          code: '44250-9',
          display: 'Little interest or pleasure in doing things',
        },
      ],
      text: 'Little interest or pleasure in doing things',
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6568-5',
            display: 'Not at all',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6569-3',
            display: 'Several days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6570-1',
            display: 'More than half the days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6571-9',
            display: 'Nearly every day',
          },
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44255-8',
          display: 'Feeling down, depressed, or hopeless',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44255-8',
      text: 'Feeling down, depressed, or hopeless',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44259-0',
          display: 'Trouble falling or staying asleep, or sleeping too much',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44259-0',
      text: 'Trouble falling or staying asleep, or sleeping too much',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44254-1',
          display: 'Feeling tired or having little energy',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44254-1',
      text: 'Feeling tired or having little energy',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44251-7',
      code: [
        {
          system: 'http://loinc.org',
          code: '44251-7',
          display: 'Poor appetite or overeating',
        },
      ],
      text: 'Poor appetite or overeating',
      answerOption: [
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6568-5',
            display: 'Not at all',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6569-3',
            display: 'Several days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6570-1',
            display: 'More than half the days',
          },
        },
        {
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6571-9',
            display: 'Nearly every day',
          },
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44258-2',
          display:
            'Feeling bad about yourself-or that you are a failure or have let yourself or your family down',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44258-2',
      text: 'Feeling bad about yourself-or that you are a failure or have let yourself or your family down',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44252-5',
          display:
            'Trouble concentrating on things, such as reading the newspaper or watching television',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44252-5',
      text: 'Trouble concentrating on things, such as reading the newspaper or watching television',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44253-3',
          display:
            'Moving or speaking so slowly that other people could have noticed. Or the opposite – being so fidgety or restless that you were moving around a lot more than usual',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44253-3',
      text: 'Moving or speaking so slowly that other people could have noticed. Or the opposite – being so fidgety or restless that you were moving around a lot more than usual',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      type: 'choice',
      code: [
        {
          code: '44260-8',
          display:
            'Thoughts that you would be better off dead, or of hurting yourself in some way',
          system: 'http://loinc.org',
        },
      ],
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/44260-8',
      text: 'Thoughts that you would be better off dead, or of hurting yourself in some way',
      answerOption: [
        {
          valueCoding: {
            code: 'LA6568-5',
            display: 'Not at all',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '0',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 0,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6569-3',
            display: 'Several days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '1',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 1,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6570-1',
            display: 'More than half the days',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '2',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 2,
            },
          ],
        },
        {
          valueCoding: {
            code: 'LA6571-9',
            display: 'Nearly every day',
            system: 'http://loinc.org',
          },
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix',
              valueString: '3',
            },
            {
              url: 'http://hl7.org/fhir/StructureDefinition/ordinalValue',
              valueDecimal: 3,
            },
          ],
        },
      ],
    },
    {
      item: [
        {
          text: 'If you checked off any problems on this questionnaire',
          type: 'display',
          linkId: '/69722-7-help',
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
              valueCodeableConcept: {
                text: 'Help-Button',
                coding: [
                  {
                    code: 'help',
                    display: 'Help-Button',
                    system: 'http://hl7.org/fhir/questionnaire-item-control',
                  },
                ],
              },
            },
          ],
        },
      ],
      type: 'choice',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
          valueCodeableConcept: {
            coding: [
              {
                system: 'http://hl7.org/fhir/questionnaire-item-control',
                code: 'drop-down',
                display: 'Drop down',
              },
            ],
            text: 'Drop down',
          },
        },
      ],
      linkId: '/69722-7',
      code: [
        {
          system: 'http://loinc.org',
          code: '69722-7',
          display:
            'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
        },
      ],
      text: 'How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?',
      answerOption: [
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6572-7',
            display: 'Not difficult at all',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6573-5',
            display: 'Somewhat difficult',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6575-0',
            display: 'Very difficult',
          },
        },
        {
          valueCoding: {
            system: 'http://loinc.org',
            code: 'LA6574-3',
            display: 'Extremely difficult',
          },
        },
      ],
    },
    {
      item: [
        {
          text: 'The PHQ-9 is the standard (and most commonly used) depression measure, and it ranges from 0-27 Scoring: Add up all checked boxes on PHQ-9. For every check: Not at all = 0; Several days = 1; More than half the days = 2; Nearly every day = 3 (the scores are the codes that appear in the answer list for each of the PHQ-9 problem panel terms). Interpretation: 1-4 = Minimal depression; 5-9 = Mild depression; 10-14 = Moderate depression; 15-19 = Moderately severe depression; 20-27 = Severe depression.',
          type: 'display',
          linkId: '/44261-6-help',
          extension: [
            {
              url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
              valueCodeableConcept: {
                text: 'Help-Button',
                coding: [
                  {
                    code: 'help',
                    display: 'Help-Button',
                    system: 'http://hl7.org/fhir/questionnaire-item-control',
                  },
                ],
              },
            },
          ],
        },
      ],
      type: 'decimal',
      extension: [
        {
          url: 'http://hl7.org/fhir/StructureDefinition/questionnaire-unit',
          valueCoding: {
            system: 'http://unitsofmeasure.org',
            code: '{score}',
            display: '{score}',
          },
        },
      ],
      linkId: '/44261-6',
      code: [
        {
          system: 'http://loinc.org',
          code: '44261-6',
          display: 'Patient health questionnaire 9 item total score',
        },
      ],
      text: 'Patient health questionnaire 9 item total score',
    },
  ],
} satisfies Questionnaire

export type Phq9Category =
  | 'Severe Depression'
  | 'Moderately Severe Depression'
  | 'Moderate Depression'
  | 'Mild Depression'
  | 'Minimal Depression'
  | 'No Depression'

export function phq9Category(score: number): Phq9Category | undefined {
  if (score >= 20) {
    return 'Severe Depression'
  }
  if (score >= 15) {
    return 'Moderately Severe Depression'
  }
  if (score >= 10) {
    return 'Moderate Depression'
  }
  if (score >= 5) {
    return 'Mild Depression'
  }
  if (score >= 1) {
    return 'Minimal Depression'
  }
  if (score === 0) {
    return 'No Depression'
  }
  return undefined
}
